import { DndContext } from '@dnd-kit/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { io } from 'socket.io-client';
import ApplicationReview from './components/ApplicationReview';
import CustomFormBuilder from './components/CustomFormBuilder';
import CustomForms from './components/CustomForms';
import Fish from './components/Fish';
import FlowBuilder from './components/FlowBuilder';
import FlowManager from './components/FlowManager';
import Layout from './components/Layout';
import Members from './components/Members';
import FishDetails from './views/fish-details';
import MessageTemplateDetails from './components/MessageTemplateDetails';
import MessageTemplates from './components/MessageTemplates';
import Campaign from './components/Campaign';
import CampaignDetails from './components/CampaignDetails';
import InviteCodes from '@Components/InviteCodes';
import SegmentBuilder from './components/SegmentBuilder';
import Segments from './components/Segments';
import Tags from './components/Tags';
import TriggerManager from './components/TriggerManager';
import AuthProvider from './contexts/auth';
import SecretsProvider from './components/SecretsProvider';
import TenantGate from './components/TenantGate';
import { useSecret } from './stores/secretsStore';
import ErrorBoundary from './components/ErrorBoundary';
import FishContextProvider from './contexts/fish';
import FlowContextProvider from './contexts/flow';
import FlowManagerContextProvider from './contexts/flowManager';
import FlowStageContextProvider from './contexts/flowStage';
import { InboxProvider } from './contexts/inbox';
import MessagesContextProvider from './contexts/messages';
import { MessageTemplatesProvider } from './contexts/messageTemplates';
import { ModalProvider } from './contexts/modal';
import TagAndSegmentContextProvider from './contexts/tagAndSegment';
import Login from './views/auth/login';
import InboxView from './views/inbox';
import UserRoles from './views/user-roles';
import Users from '@Components/Users';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Venues from './views/venues';
import CreateVenue from './views/venues/create';
import VenueDetails from './views/venues/venue-details';
import Spaces from './views/spaces';
import CreateSpace from './views/spaces/create';
import SpaceDetails from './views/spaces/space-details';
import FeatureMatrix from '@Views/feature-matrix';
import Purchases from './views/purchases';
import PurchaseDetails from './views/purchases/purchase-details';
import Orders from './views/shop/orders';
import OrderDetails from './views/shop/orders/order-details';
import SubscriptionTiers from '@Components/SubscriptionTiers';
import SubscriptionTierDetails from '@Components/SubscriptionTierDetails';
import {
  Toaster,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { CloudinaryProvider } from '@art-of-hospitality-solutions-llc/ui-kit/contexts/cloudinary-context';
import TicketedEvents from '@Views/ticketed-events';
import CreateTicketedEvent from '@Views/ticketed-events/create';
import TicketedEventDetails from '@Views/ticketed-events/ticketed-events-details';
import TicketedEventCategories from '@Views/ticketed-events/categories';
import TeamMembers from '@Components/TeamMembers';
import Insights from '@Views/insights';
import Locations from '@Views/locations';
import Events from '@Views/events';
import EventsListView from '@Views/events/list';
import CreateEvent from '@Views/events/create';
import EventCategories from '@Views/events/categories';
import EventTags from '@Views/events/tags';
import EventOrganizers from '@Views/events/organizers';
import EventOrganizerDetails from '@Views/events/organizers/organizer-details';
import EventDetails from '@Views/events/event-details';
import TicketsList from '@Views/events/tickets';
import TicketPurchaseDetails from '@Views/events/tickets/ticket-purchase-details';
import ToastGuids from '@Views/settings/toast-guids';
import ToastGuidGroups from '@Views/settings/toast-guid-groups';
import ToastGuidSettings from '@Views/settings/toast-guid-settings';
import ToastGuidGroupDetails from '@Views/settings/toast-guid-groups/toast-guid-group-details';
import Assets from '@Views/assets';
import AssetTags from '@Views/asset-tags';
import PromoCodes from '@Views/promo-codes';
import PromoCodeDetails from '@Views/promo-codes/promo-code-details';
import ProtectedRoute from '@Components/ProtectedRoute';
import HomeRedirect from '@Components/HomeRedirect';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import EventsCalendar from '@Views/events/calendar';
import InviteCodesDetails from '@Components/InviteCodesDetails';
import PartnerReferralCodes from '@Components/PartnerReferralCodes';
import PartnerReferralCodeDetails from '@Components/PartnerReferralCodeDetails';
import UserRoleDetails from '@Views/user-roles/user-role-details';
import Souls from '@Views/souls';
import OmniSearch from '@Views/omni-search';
import CreateSoul from '@Views/souls/create';
import ActivityTest from '@Views/activity/test';
import SoulDetails from '@Views/souls/soul-details';
import AgentDesk from '@Views/souls/desk';
import AIChat from '@Views/ai-chat';
import Evals from '@Views/evals';
import CreateEvalSuite from '@Views/evals/create';
import SuiteDetails from '@Views/evals/suite-details';
import CaseForm from '@Views/evals/case-form';
import RunDetails from '@Views/evals/run-details';
import JobRuns from '@Views/job-runs';
import JobRunTypeStats from '@Views/job-runs/job-run-type-stats';
import JobRunDetails from '@Views/job-runs/job-run-details';
import EngagementStatsPage from '@Views/engagement-stats';
import InboxManagement from '@Views/inbox-management';
import PhoneNumbers from '@Views/phone-numbers';
import AdminCache from '@Views/admin-cache';
import EventsProtectedLayout from '@Views/events/layout';
import TicketedEventsProtectedLayout from '@Views/ticketed-events/layout';
import PrivateDiningEvents from '@Views/private-dining-events';
import PrivateDiningEventDetails from '@Views/private-dining-events/private-dining-details';
import EventsMain from '@Views/events';
import TicketedEventPreview from '@Views/ticketed-events/ticketed-events-details/preview';
import FlowSamplePage from '@Views/flows/flow-sample';
import FlowListView from '@Views/flows/flows-list-view';
import Flows from '@Views/flows';
import FlowCategories from '@Components/FlowCategories';
import Dashboards from '@Components/Dashboards';
import Workspaces from '@Views/workspaces';
import WorkspaceManager from '@Components/WorkspaceManager';
import WorkspaceCards from '@Components/WorkspaceCards';
import WorkspaceViewer from '@Components/WorkspaceViewer';
import FlowCategoryCards from '@Components/FlowCategoryCards';
import { FlowCategoryDetails } from '@Components/FlowCategoryDetails';
import CommandCenter from '@Views/command-center';
import ContentDetails from '@Views/content-editor/content-details';
import ContentEditorList from '@Views/content-editor';
import OmniAgent from '@Views/omni-agent';

const queryClient = new QueryClient();

function AppContent() {
  return (
    <>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route
          path="ticketed-events/preview"
          element={<TicketedEventPreview />}
        />
        <Route
          path="/flows/list/:flowId"
          element={
            <DndContext>
              <FlowSamplePage />
            </DndContext>
          }
        />
        <Route
          path="/*"
          element={
            <DndContext>
              <Layout>
                <Routes>
                  <Route index element={<HomeRedirect />} />

                  <Route
                    path="command-center"
                    element={
                      <ProtectedRoute roles={[]}>
                        <CommandCenter />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="members"
                    element={
                      <ProtectedRoute permissions={['MEMBERS_READ']}>
                        <Members />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="members/:id"
                    element={
                      <ProtectedRoute permissions={['MEMBERS_READ']}>
                        <FishDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="omni-search"
                    element={
                      <ProtectedRoute permissions={['MEMBERS_READ']}>
                        <OmniSearch />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/events" element={<EventsProtectedLayout />}>
                    <Route index element={<EventsMain />} />
                    <Route index element={<Events />} />
                    <Route path="calendar" element={<EventsCalendar />} />
                    <Route path="general" element={<EventsListView />} />
                    <Route path="create" element={<CreateEvent />} />
                    <Route path="categories" element={<EventCategories />} />
                    <Route path="tags" element={<EventTags />} />
                    <Route path="organizers" element={<EventOrganizers />} />
                    <Route
                      path="organizers/:organizerId"
                      element={<EventOrganizerDetails />}
                    />
                    <Route path=":eventId" element={<EventDetails />} />
                    <Route path="tickets" element={<TicketsList />} />
                    <Route
                      path="tickets/purchases/:purchaseId"
                      element={<TicketPurchaseDetails />}
                    />
                  </Route>

                  <Route
                    path="/ticketed-events"
                    element={<TicketedEventsProtectedLayout />}
                  >
                    <Route index element={<TicketedEvents />} />

                    <Route path="create" element={<CreateTicketedEvent />} />
                    <Route
                      path="categories"
                      element={<TicketedEventCategories />}
                    />

                    <Route path=":eventId" element={<TicketedEventDetails />} />
                  </Route>

                  <Route
                    path="insights/*"
                    element={
                      <ProtectedRoute permissions={['INSIGHTS_READ']}>
                        <Insights />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="guests/*"
                    element={
                      <ProtectedRoute permissions={['GUESTS_READ']}>
                        <Fish />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="guests/:id"
                    element={
                      <ProtectedRoute permissions={['GUESTS_READ']}>
                        <FishDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="tags/*"
                    element={
                      <ProtectedRoute permissions={['TAGS_READ']}>
                        <Tags />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="segments/*"
                    element={
                      <ProtectedRoute permissions={['SEGMENTS_READ']}>
                        <Segments />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="segments/new"
                    element={
                      <ProtectedRoute permissions={['SEGMENTS_READ']}>
                        <SegmentBuilder mode="create" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="segments/:id"
                    element={
                      <ProtectedRoute permissions={['SEGMENTS_READ']}>
                        <SegmentBuilder mode="edit" />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="inbox"
                    element={
                      <ProtectedRoute permissions={['INBOX_READ']}>
                        <InboxProvider>
                          <InboxView />
                        </InboxProvider>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="engagement-stats"
                    element={
                      <ProtectedRoute permissions={['INBOX_MANAGE']}>
                        <EngagementStatsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="inbox-management"
                    element={
                      <ProtectedRoute permissions={['INBOX_MANAGE']}>
                        <InboxManagement />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="phone-numbers"
                    element={
                      <ProtectedRoute permissions={['NUMBER_READ']}>
                        <PhoneNumbers />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="campaigns"
                    element={
                      <ProtectedRoute permissions={['CAMPAIGNS_READ']}>
                        <Campaign />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="campaigns/:id"
                    element={
                      <ProtectedRoute permissions={['CAMPAIGNS_READ']}>
                        <CampaignDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="message-templates/*"
                    element={
                      <ProtectedRoute permissions={['MESSAGE_TEMPLATES_READ']}>
                        <MessageTemplates />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="message-templates/:id"
                    element={
                      <ProtectedRoute permissions={['MESSAGE_TEMPLATES_READ']}>
                        <MessageTemplateDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/locations"
                    element={
                      <ProtectedRoute permissions={['LOCATIONS_READ']}>
                        <Locations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venues"
                    element={
                      <ProtectedRoute permissions={['VENUES_READ']}>
                        <Venues />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venues/create"
                    element={
                      <ProtectedRoute permissions={['VENUES_WRITE']}>
                        <CreateVenue />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/venues/:venueId"
                    element={
                      <ProtectedRoute permissions={['VENUES_READ']}>
                        <VenueDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/spaces"
                    element={
                      <ProtectedRoute permissions={['SPACES_READ']}>
                        <Spaces />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/spaces/create"
                    element={
                      <ProtectedRoute permissions={['SPACES_WRITE']}>
                        <CreateSpace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/spaces/:spaceId"
                    element={
                      <ProtectedRoute permissions={['SPACES_READ']}>
                        <SpaceDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/feature-matrix"
                    element={
                      <ProtectedRoute
                        permissions={['VENUES_READ', 'SPACES_READ']}
                      >
                        <FeatureMatrix />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/private-dining-events"
                    element={
                      <ProtectedRoute permissions={['EVENTS_READ']}>
                        <PrivateDiningEvents />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/private-dining-events/:eventId"
                    element={
                      <ProtectedRoute permissions={['EVENTS_READ']}>
                        <PrivateDiningEventDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/shop/orders"
                    element={
                      <ProtectedRoute permissions={['SHOP_READ']}>
                        <Orders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop/orders/:orderId"
                    element={
                      <ProtectedRoute permissions={['SHOP_READ']}>
                        <OrderDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="trigger-manager/*"
                    element={
                      <ProtectedRoute permissions={['TRIGGER_READ']}>
                        <TriggerManager />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flows"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <Flows />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/flows/list"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <DndContext>
                          <FlowListView />
                        </DndContext>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flows/categories"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <FlowCategories />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flows/cards"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <FlowCategoryCards />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flows/cards/:id"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <FlowCategoryDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flows/builder/*"
                    element={
                      <ProtectedRoute
                        permissions={[
                          'FLOW_BUILDER_READ',
                          'FLOW_BUILDER_WRITE',
                        ]}
                      >
                        <FlowBuilder />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="flow-manager/*"
                    element={
                      <ProtectedRoute permissions={['FLOW_READ']}>
                        <FlowManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="review/*"
                    element={
                      <ProtectedRoute permissions={['FLOW_WRITE']}>
                        <ApplicationReview />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="custom-form-builder"
                    element={
                      <ProtectedRoute permissions={['FORM_READ']}>
                        <CustomForms />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="custom-form-builder/new"
                    element={
                      <ProtectedRoute permissions={['FORM_WRITE']}>
                        <CustomFormBuilder />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="custom-form-builder/:id"
                    element={
                      <ProtectedRoute permissions={['FORM_WRITE']}>
                        <CustomFormBuilder />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="omni-agent"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <OmniAgent />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="dashboards"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <Dashboards />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="workspaces"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <Workspaces />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="workspaces/manager"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <WorkspaceManager />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="workspaces/cards"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <WorkspaceCards />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="workspaces/manager/:id"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <WorkspaceViewer />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="workspaces/cards/:id"
                    element={
                      <ProtectedRoute permissions={['DASHBOARD_WRITE']}>
                        <WorkspaceViewer />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="users/*"
                    element={
                      <ProtectedRoute permissions={['USER_ROLES_WRITE']}>
                        <Users />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="user-roles"
                    element={
                      <ProtectedRoute permissions={['USER_ROLES_WRITE']}>
                        <UserRoles />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="user-roles/:userRoleId"
                    element={
                      <ProtectedRoute permissions={['USER_ROLES_WRITE']}>
                        <UserRoleDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="team-members"
                    element={
                      <ProtectedRoute permissions={['TEAM_MEMBERS_READ']}>
                        <TeamMembers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="team-members/:teamMemberId"
                    element={
                      <ProtectedRoute
                        permissions={['TEAM_MEMBERS_READ']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <FishDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/purchases">
                    <Route
                      index
                      element={
                        <ProtectedRoute
                          roles={['ADMIN_ROLE']}
                          checkEveryPermission={false}
                          checkEveryRole={false}
                        >
                          <Purchases />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":purchaseId"
                      element={
                        <ProtectedRoute
                          roles={['ADMIN_ROLE']}
                          checkEveryPermission={false}
                          checkEveryRole={false}
                        >
                          <PurchaseDetails />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="/subscription-tiers"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <SubscriptionTiers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription-tiers/:id"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <SubscriptionTierDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/cache"
                    element={
                      <ProtectedRoute roles={['ADMIN_ROLE']}>
                        <AdminCache />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invite-codes"
                    element={
                      <ProtectedRoute
                        permissions={[
                          'REGISTRATION_CODES_READ',
                          'REGISTRATION_CODES_WRITE',
                        ]}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <InviteCodes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invite-codes/:id"
                    element={
                      <ProtectedRoute
                        permissions={[
                          'REGISTRATION_CODES_READ',
                          'REGISTRATION_CODES_WRITE',
                        ]}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <InviteCodesDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/partner-referral-codes"
                    element={
                      <ProtectedRoute
                        permissions={[
                          'REGISTRATION_CODES_READ',
                          'REGISTRATION_CODES_WRITE',
                        ]}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <PartnerReferralCodes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/partner-referral-codes/:id"
                    element={
                      <ProtectedRoute
                        permissions={[
                          'REGISTRATION_CODES_READ',
                          'REGISTRATION_CODES_WRITE',
                        ]}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <PartnerReferralCodeDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/agents">
                    <Route
                      index
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <Souls />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="create"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <CreateSoul />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":soulId"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <AgentDesk />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":soulId/edit"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <SoulDetails />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route path="/evals">
                    <Route
                      index
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <Evals />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="create"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <CreateEvalSuite />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":suiteId"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <SuiteDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":suiteId/cases/new"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <CaseForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":suiteId/cases/:caseId/edit"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <CaseForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="runs/:runId"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <RunDetails />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route path="/activity">
                    <Route
                      path="test"
                      element={
                        <ProtectedRoute roles={['ADMIN_ROLE']}>
                          <ActivityTest />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  <Route
                    path="/ai-chat"
                    element={
                      <ProtectedRoute roles={['ADMIN_ROLE']}>
                        <AIChat />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/job-runs"
                    element={
                      <ProtectedRoute roles={['ADMIN_ROLE']}>
                        <JobRuns />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/job-runs/type/:typeName"
                    element={
                      <ProtectedRoute roles={['ADMIN_ROLE']}>
                        <JobRunTypeStats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/job-runs/:jobRunId"
                    element={
                      <ProtectedRoute roles={['ADMIN_ROLE']}>
                        <JobRunDetails />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings/toast-guids"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <ToastGuids />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/toast-guid-groups"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <ToastGuidGroups />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/toast-guid-settings"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <ToastGuidSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/toast-guid-groups/:toastGuidGroupId"
                    element={
                      <ProtectedRoute
                        roles={['ADMIN_ROLE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <ToastGuidGroupDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/promo-codes"
                    element={
                      <ProtectedRoute
                        permissions={['PROMO_CODES_READ', 'PROMO_CODES_WRITE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <PromoCodes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/promo-codes/:promoCodeId"
                    element={
                      <ProtectedRoute
                        permissions={['PROMO_CODES_READ', 'PROMO_CODES_WRITE']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <PromoCodeDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/asset-manager"
                    element={
                      <ProtectedRoute
                        permissions={['ASSETS_READ']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <Assets />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/asset-tags"
                    element={
                      <ProtectedRoute
                        permissions={['ASSETS_READ']}
                        checkEveryPermission={false}
                        checkEveryRole={false}
                      >
                        <AssetTags />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/content-editor">
                    <Route
                      index
                      element={
                        <ProtectedRoute permissions={['CONTENT_READ']}>
                          <ContentEditorList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":appSlug"
                      element={
                        <ProtectedRoute permissions={['CONTENT_READ']}>
                          <ContentDetails />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  {/* replace with Access Denied page */}
                  <Route path="/forbidden" element={<div>Access Denied</div>} />
                </Routes>
              </Layout>
            </DndContext>
          }
        />
      </Routes>
    </>
  );
}

function CloudinaryShell({ children }: { children: React.ReactNode }) {
  const cloudName = useSecret('CLOUDINARY_CLOUDNAME') ?? '';
  return (
    <CloudinaryProvider cloudName={cloudName}>{children}</CloudinaryProvider>
  );
}

function App() {
  useEffect(() => {
    //May need to be updated for when production and staging have paths as well
    const socket = io(import.meta.env.VITE_API_URL, {
      transports: ['websocket'],
    });
    socket.on('connect', () => {
      console.log('socket connected');
    });
    socket.on('scriptComplete', (data) => {
      const localTime = new Date(data.timestamp).toLocaleString();
      toastSuccess(`Digest script completed at ${localTime}, ${data.message}`);
    });
    socket.on('disconnect', () => {
      console.log('Disconnected from server');
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <TenantGate>
      <BrowserRouter>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ErrorBoundary>
                <SecretsProvider>
                  <CloudinaryShell>
                    <ModalProvider>
                      <TagAndSegmentContextProvider>
                        <MessageTemplatesProvider>
                          <FishContextProvider>
                            <FlowContextProvider>
                              <FlowStageContextProvider>
                                <MessagesContextProvider>
                                  <FlowManagerContextProvider>
                                    <AppContent />
                                    <Toaster position="top-center" />
                                  </FlowManagerContextProvider>
                                </MessagesContextProvider>
                              </FlowStageContextProvider>
                            </FlowContextProvider>
                          </FishContextProvider>
                        </MessageTemplatesProvider>
                      </TagAndSegmentContextProvider>
                    </ModalProvider>
                  </CloudinaryShell>
                </SecretsProvider>
              </ErrorBoundary>
            </AuthProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </TenantGate>
  );
}

export default App;
